<template>
  <div>
    <b-modal
      id="changeStoreModal"
      ref="changeStoreModal"
      title="Thay đổi cửa hàng thiết bị"
      :size="modalSize"
      hide-footer
      @show="onModalShow"
    >
      <div v-if="isLoadingStores" class="text-center">
        <b-spinner class="align-middle" />
        <p class="text-center mt-2">Đang tải danh sách cửa hàng...</p>
      </div>

      <div v-else>
        <div class="mb-3">
          <div v-if="storeList.length === 0" class="text-center text-muted">
            Không có cửa hàng khác để chuyển
          </div>

          <div v-else class="store-section">
            <!-- Ô tìm kiếm -->
            <div class="search-box mb-3">
              <b-input-group>
                <b-form-input
                  v-model="searchKeyword"
                  placeholder="Tìm kiếm cửa hàng..."
                  @input="onSearch"
                />
                <b-input-group-append>
                  <b-button variant="outline-secondary" @click="clearSearch" v-if="searchKeyword">
                    <i class="bx bx-x"></i>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </div>

            <div class="store-list">
              <div class="store-grid">
                <div
                  v-for="store in filteredStoreList"
                  :key="store.id"
                  class="store-item"
                  :class="{ 'current-store': store.id === infoData?.storeId }"
                >
                  <b-form-radio
                    v-model="selectedStoreId"
                    :value="store.id"
                    name="store-radio"
                    @change="onStoreSelect"
                  >
                    <div class="store-content">
                      <div class="store-header">
                        <p class="store-name">{{ store.storeName }}</p>
                        <span v-if="store.id === infoData?.storeId" class="badge badge-success">
                          Hiện tại
                        </span>
                      </div>
                      <div class="store-address">
                        Địa chỉ: {{ store.address }}, {{ store.districtName }},
                        {{ store.provinceName }}
                      </div>
                      <div class="store-contact">
                        <a
                          :href="
                            store.email?.startsWith('0')
                              ? `tel:${store.email}`
                              : `mailto:${store.email}`
                          "
                        >
                          <i class="bx bx-envelope"></i>
                          <span class="contact-text">{{ store.email }}</span>
                        </a>
                      </div>
                    </div>
                  </b-form-radio>
                </div>
              </div>
              <div v-if="filteredStoreList.length === 0">
                <p class="text-center text-muted">Không tìm thấy cửa hàng</p>
              </div>
            </div>
          </div>
        </div>

        <div class="action-buttons">
          <button type="button" class="btn btn-secondary" @click="closeModal">Hủy</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!selectedStoreId || isChangingStore || selectedStoreId === infoData?.storeId"
            @click="changeStore"
          >
            <b-spinner v-if="isChangingStore" small class="mr-1" />
            Thay đổi
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import { showMess } from '@/config/utils'
  import { DeviceService } from '@/services/device.service'

  export default {
    data() {
      return {
        isLoadingStores: false,
        isChangingStore: false,
        storeList: [],
        selectedStoreId: null,
        infoData: {},
        searchKeyword: '',
      }
    },
    computed: {
      filteredStoreList() {
        if (!this.searchKeyword.trim()) {
          return this.storeList
        }

        const keyword = this.searchKeyword.toLowerCase().trim()
        return this.storeList.filter(store => {
          return (
            store.storeName?.toLowerCase().includes(keyword) ||
            store.address?.toLowerCase().includes(keyword) ||
            store.email?.toLowerCase().includes(keyword) ||
            store.mobile?.toLowerCase().includes(keyword)
          )
        })
      },
      modalSize() {
        // Responsive modal size
        return window.innerWidth <= 768 ? 'sm' : 'lg'
      },
    },
    methods: {
      show(infoData) {
        this.infoData = infoData || {}
        this.$refs.changeStoreModal.show()
      },

      closeModal() {
        this.$refs.changeStoreModal.hide()
        this.resetForm()
      },

      resetForm() {
        this.selectedStoreId = null
        this.storeList = []
        this.searchKeyword = ''
      },

      async onModalShow() {
        await this.loadStores()
      },

      async loadStores() {
        try {
          this.isLoadingStores = true

          const maId = this.infoData?.mcAccId
          if (!maId) {
            showMess(this, 'Lỗi', 'Không có MA', 'danger', 'b-toaster-top-right', 5000)
            setTimeout(() => {
              this.closeModal()
            }, 500)
            return
          }

          const queryString = `{mcAccountId} == '''${maId}'''`
          const response = await DeviceService.getStoreForDevice({
            queryString,
            limit: 1000,
            page: 1,
          })

          this.storeList = response.data?.listData || []
          this.selectedStoreId = this.infoData?.storeId

          if (this.storeList.length === 0) {
            showMess(
              this,
              'Thông báo',
              'Không có cửa hàng để chuyển',
              'warning',
              'b-toaster-top-right',
              5000
            )
          }
        } catch (error) {
          showMess(
            this,
            'Lỗi',
            error.message || 'Có lỗi xảy ra khi tải danh sách cửa hàng',
            'danger',
            'b-toaster-top-right',
            5000
          )
        } finally {
          this.isLoadingStores = false
        }
      },

      onSearch() {
        if (
          this.selectedStoreId &&
          !this.filteredStoreList.find(store => store.id === this.selectedStoreId)
        ) {
          this.selectedStoreId = null
        }
      },

      clearSearch() {
        this.searchKeyword = ''
      },

      onStoreSelect() {
        if (this.selectedStoreId === this.infoData?.storeId) {
          showMess(
            this,
            'Cảnh báo',
            'Bạn đã chọn cửa hàng hiện tại. Vui lòng chọn cửa hàng khác để thay đổi.',
            'warning',
            'b-toaster-top-right',
            5000
          )
        }
      },

      async changeStore() {
        if (this.selectedStoreId === this.infoData?.storeId) {
          showMess(
            this,
            'Lỗi',
            'Không thể chuyển về cửa hàng hiện tại',
            'danger',
            'b-toaster-top-right',
            5000
          )
          return
        }

        try {
          this.isChangingStore = true

          const payload = {
            deviceStoreId: this.infoData?.id || this.$route.params.id,
            storeId: this.selectedStoreId,
          }

          const res = await DeviceService.ChangeStoreDevice(payload)
          if (res.code === 1000) {
            showMess(
              this,
              'Thành công',
              'Thay đổi cửa hàng thiết bị thành công',
              'success',
              'b-toaster-top-right',
              5000
            )

            this.closeModal()
            this.$emit('store-changed')
          } else {
            showMess(
              this,
              'Lỗi',
              res.message || 'Có lỗi xảy ra khi thay đổi cửa hàng',
              'danger',
              'b-toaster-top-right',
              5000
            )
          }
        } catch (error) {
          showMess(
            this,
            'Lỗi',
            error.message || 'Có lỗi xảy ra khi thay đổi cửa hàng',
            'danger',
            'b-toaster-top-right',
            5000
          )
        } finally {
          this.isChangingStore = false
        }
      },
    },
  }
</script>

<style scoped>
  /* Base styles */
  .store-section {
    display: flex;
    flex-direction: column;
  }

  .search-box {
    width: 100%;
    max-width: 400px;
  }

  .store-list {
    height: 500px;
    max-height: 600px;
    overflow-y: auto;
    padding: 0 5px;
  }

  .store-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .store-item {
    padding: 12px;
    border: 1px solid #e3e6f0;
    border-radius: 8px;
    transition: all 0.2s ease;
    background-color: #fff;
  }

  .store-item:hover {
    background-color: #f8f9fa;
    border-color: #007bff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .store-content {
    margin-left: 8px;
  }

  .store-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  .store-name {
    font-size: 16px;
    font-weight: 500;
    margin: 0;
    color: #2c3e50;
  }

  .store-address {
    font-size: 13px;
    margin-bottom: 8px;
    color: #6c757d;
    line-height: 1.4;
  }

  .store-contact {
    font-size: 12px;
  }

  .store-contact a {
    color: #007bff;
    text-decoration: none;
    display: flex;
    align-items: center;
  }

  .store-contact a:hover {
    text-decoration: underline;
  }

  .store-contact i {
    margin-right: 6px;
    font-size: 14px;
  }

  .contact-text {
    word-break: break-all;
  }

  .current-store {
    background-color: #e7f3ff;
    border-left: 4px solid #007bff;
    opacity: 0.8;
  }

  .current-store .store-content {
    color: #6c757d;
  }

  .badge-success {
    background-color: #28a745;
    color: white;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
  }

  .action-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 20px;
  }

  .btn {
    padding: 8px 20px;
    border-radius: 6px;
    font-weight: 500;
    min-width: 80px;
  }

  /* Mobile Responsive Styles */
  @media (max-width: 768px) {
    .store-grid {
      grid-template-columns: 1fr;
      gap: 8px;
    }

    .store-item {
      padding: 10px;
    }

    .store-name {
      font-size: 15px;
    }

    .store-address {
      font-size: 12px;
    }

    .store-contact {
      font-size: 11px;
    }

    .store-list {
      height: 350px;
      max-height: 400px;
      padding: 0 2px;
    }

    .search-box {
      margin-bottom: 15px;
    }

    .search-box input {
      font-size: 16px; /* Prevent zoom on iOS */
    }

    .action-buttons {
      flex-direction: column-reverse;
      gap: 8px;
      margin-top: 15px;
    }

    .btn {
      width: 100%;
      padding: 12px;
      font-size: 16px;
    }

    .badge-success {
      font-size: 10px;
      padding: 3px 6px;
    }

    .store-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
    }

    .store-header .badge-success {
      align-self: flex-start;
    }
  }

  @media (max-width: 480px) {
    .store-item {
      padding: 8px;
    }

    .store-name {
      font-size: 14px;
    }

    .store-address {
      font-size: 11px;
    }

    .store-contact {
      font-size: 10px;
    }

    .store-list {
      height: 300px;
      max-height: 350px;
    }

    .search-box input::placeholder {
      font-size: 14px;
    }
  }

  /* Custom radio button styles */
  .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #007bff;
    border-color: #007bff;
  }

  .custom-control-input:disabled ~ .custom-control-label {
    color: #6c757d;
  }

  .custom-control-input:disabled ~ .custom-control-label::before {
    background-color: #e9ecef;
    border-color: #dee2e6;
  }

  /* Scrollbar styling */
  .store-list::-webkit-scrollbar {
    width: 6px;
  }

  .store-list::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  .store-list::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  .store-list::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
</style>
